// ─────────────────────────────────────────────────────────────────────
//  Prefill des FORMULAIRES publics depuis l'Espace de gestion de compte.
//
//  Périmètre V1 (volontairement minimal — cf. décision produit) :
//    - On préremplit UNIQUEMENT le bloc identité principal (data.identity), avec
//      le contact du TITULAIRE (selfContact). Pas de choix de membre (V2), pas de
//      sélecteur de contact (V2, qui pourra cibler un bloc [CONTACT]_ imbriqué).
//    - Adresse : la 1ʳᵉ adresse du profil (pas de choix, pas de création ici).
//    - Champs custom (extras) : injectés par matching de CLÉ. Comme un champ custom
//      peut être posé soit à la RACINE du form (name = "[TYPE]_clé") soit DANS le
//      bloc identité (name = ["identity","[TYPE]_clé"]), on « arrose » les DEUX
//      emplacements d'initialValues. Antd ne remplit que les champs réellement
//      présents → aucun effet de bord sur les clés sans champ correspondant.
//
//  Module PUR (aucune dépendance React/réseau) — réutilisable web ET admin.
//  Le déclencheur (chargement /me, qui décide quand/quel contact) vit côté
//  composant (FormEditorRender). Ici : transformation profil → initialValues.
// ─────────────────────────────────────────────────────────────────────

import {buildInitialValuesFromProfile} from "./api"

// Champs d'identité plats du module form-identity/ (sous data.identity).
// L'adresse (mode FULL) est UNE adresse à plat : adresse/postal/ville/etat/pays.
function identityFromProfile(profile = {}) {
    const out = {}
    if (profile.homme === true || profile.homme === false) out.homme = profile.homme
    if (profile.prenom) out.prenom = profile.prenom
    if (profile.nom) out.nom = profile.nom
    if (profile.email) out.email = profile.email
    if (profile.tel) out.tel = profile.tel

    // 1ʳᵉ adresse du profil → champs adresse plats (utiles seulement si le form est
    // en mode FULL ; sinon Antd ignore ces clés, sans effet).
    const adresses = Array.isArray(profile.adresses) ? profile.adresses : []
    if (adresses.length > 0) {
        const a = adresses[0]
        if (a.adresse) out.adresse = a.adresse
        if (a.postal) out.postal = a.postal
        if (a.ville) out.ville = a.ville
        if (a.etat) out.etat = a.etat
        if (a.pays) out.pays = a.pays
    }
    return out
}

/**
 * Construit les `defaultData` (initialValues du <Form>) pour préremplir un
 * formulaire public depuis le profil + selfContact du titulaire.
 *
 * @param {object} account {selfContact, profile:{nom,prenom,email,tel,homme,adresses[],extras{}}}
 * @param {object} [base]  données de départ (ex: {__paiementMethod:"En Ligne"} ou le
 *   prefill localStorage existant) — on construit PAR-DESSUS sans l'écraser à l'aveugle.
 * @returns {object} defaultData enrichi (identité sous .identity, extras arrosés
 *   racine + identity, id du contact pour la garde A7).
 */
export function buildFormPrefill(account, base = {}) {
    if (!account || !account.profile) return {...base}
    const profile = account.profile || {}
    const out = {...base}

    // 1. Identité (sous data.identity) — fusionnée avec une éventuelle identité de base.
    //    NB : on ne pose PAS identity.id ici. Le module identité n'a aucun
    //    <Form.Item name=["identity","id"]>, donc Antd ne conserverait pas un id
    //    injecté dans initialValues (il ne remonte que les champs enregistrés). Le
    //    rattachement au contact est posé au submit (FormEditorRender.onSubmit) et
    //    revalidé côté serveur (garde A7).
    const identity = {
        ...(base.identity || {}),
        ...identityFromProfile(profile),
    }

    out.identity = identity

    // 3. Champs custom (extras) — convertis par TYPE via buildInitialValuesFromProfile
    //    (NUMBER→Number, SELECT_MULTI→array, DATE→Date, DATETIME/…→dayjs, reste tel quel).
    //    On récupère uniquement les clés "[TYPE]_…" (la clé `identity` produite par le
    //    helper porte l'identité, qu'on a déjà posée ci-dessus).
    const converted = buildInitialValuesFromProfile(profile)
    for (const key of Object.keys(converted)) {
        if (key === "identity") continue
        const value = converted[key]
        // ARROSAGE DES 2 EMPLACEMENTS : racine ("[TYPE]_clé") ET sous identity
        // (["identity","[TYPE]_clé"]). Un champ custom rattaché au bloc identité est
        // nested sous identity ; un champ custom à la racine du form est à plat.
        if (out[key] === undefined) out[key] = value
        if (out.identity[key] === undefined) out.identity[key] = value
    }

    return out
}
